import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography } from "@mui/material";
import HealthMeasurementGauge from "@/features/MyHealth/components/HealthMeasurementGauge";
import {
  getHealthMeasurementValueRange,
  type HealthMeasurementQuestionType,
} from "@/services/core-api-adapter";
import { HealthMeasurementIdentifier } from "@/store";
import { getHealthMeasurementValueRangeColors } from "@/theme";

const START_ANGLE = 90;
const END_ANGLE = 270;
const ARC_DEGREES = END_ANGLE - START_ANGLE;

const SEGMENT_ARC_WEIGHTS = [
  { min: 60, max: 80, weight: 2 },
  { min: 80, max: 100, weight: (9 / 13) * 2 },
  { min: 100, max: 140, weight: (8 / 13) * 2 },
  { min: 140, max: 160, weight: 1 },
  { min: 160, max: 180, weight: 5 / 7 },
];
const TOTAL_WEIGHT = SEGMENT_ARC_WEIGHTS.reduce((s, seg) => s + seg.weight, 0);

function heartRateValueToAngle(value: number): number {
  if (value <= 60) return START_ANGLE;
  if (value >= 180) return END_ANGLE;
  let accumulated = 0;
  for (const seg of SEGMENT_ARC_WEIGHTS) {
    if (value <= seg.max) {
      const t = (value - seg.min) / (seg.max - seg.min);
      return (
        START_ANGLE +
        ((accumulated + t * seg.weight) / TOTAL_WEIGHT) * ARC_DEGREES
      );
    }
    accumulated += seg.weight;
  }
  return END_ANGLE;
}

function segmentTicks(from: number, to: number, count: number): number[] {
  const out: number[] = [];
  for (let i = 0; i <= count; i++) {
    out.push(from + (to - from) * (i / count));
  }
  return out;
}

function getHeartRateTickValues(): number[] {
  const a = segmentTicks(60, 80, 12);
  const b = segmentTicks(80, 100, 8);
  const c = segmentTicks(100, 140, 7);
  const d = segmentTicks(140, 160, 6);
  const e = segmentTicks(160, 180, 4);
  return Array.from(new Set([...a, ...b, ...c, ...d, ...e])).sort(
    (x, y) => x - y
  );
}

const HEART_RATE_TICK_VALUES = getHeartRateTickValues();
const HEART_RATE_TICK_LABELS = [60, 80, 100, 140, 180];

function getValueRangeWithColors() {
  const valueRange = getHealthMeasurementValueRange("Pulse");
  const colorMap = getHealthMeasurementValueRangeColors() as Record<
    string,
    Record<string, string>
  >;
  const key: HealthMeasurementQuestionType = "Pulse";
  return valueRange.map((item) => ({
    ...item,
    color: colorMap[key]?.[item.healthRiskIndicator],
  }));
}

export interface CurrentHeartRateProps {
  value: number;
  unit?: string;
}

export default function CurrentHeartRate({
  value,
  unit = "",
}: CurrentHeartRateProps): JSX.Element {
  const { t } = useTranslation();
  const valueRange = useMemo(() => getValueRangeWithColors(), []);

  return (
    <Stack alignItems="center">
      <HealthMeasurementGauge
        healthMeasurementIdentifier={HealthMeasurementIdentifier.Pulse}
        value={value}
        unit={unit}
        valueRange={valueRange}
        numberOfTicks={HEART_RATE_TICK_VALUES.length}
        tickValues={HEART_RATE_TICK_VALUES}
        tickLabelValues={HEART_RATE_TICK_LABELS}
        valueToAngle={heartRateValueToAngle}
      />
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 16,
          lineHeight: 1.25,
        }}
      >
        {t("HeartRate.manualEntry.inputLabel")}
      </Typography>
    </Stack>
  );
}
